import { ReactNode } from 'react';
import { CheckCircle, GitMerge, XCircle } from 'lucide-react';

import { truncateLeftRight } from '@/portainer/filters/filters';
import { baseStackWebhookUrl } from '@/portainer/helpers/webhookHelper';
import { GitCommitLink } from '@/react/portainer/gitops/GitCommitLink';
import { useCheckRepo } from '@/react/portainer/gitops/queries/useCheckRepo';
import { useGitRefs } from '@/react/portainer/gitops/queries/useGitRefs';
import { useSearch } from '@/react/portainer/gitops/queries/useSearch';
import { AutomationTestingProps } from '@/types';
import PortainerError from '@/portainer/error';
import {
  AutoUpdateResponse,
  RepoConfigResponse,
} from '@/react/portainer/gitops/types';

import { CopyButton } from '@@/buttons';
import { Card } from '@@/Card';
import { Icon } from '@@/Icon';
import { Alert } from '@@/Alert';

export function GitReferenceCard({
  stackId,
  stackType = 'docker',
  gitConfig,
  autoUpdate,
}: {
  stackId: number;
  stackType?: 'docker' | 'helm' | 'edge';
  gitConfig?: RepoConfigResponse | null;
  autoUpdate?: AutoUpdateResponse | null;
}) {
  const {
    ConfigHash: commitID,
    ReferenceName: reference,
    URL: url,
    ConfigFilePath: configFilePath,
  } = gitConfig || {};

  const fromEdgeStack = stackType === 'edge';

  // check repo
  const repoCheckQuery = useCheckRepo(
    url || '',
    { stackId, fromEdgeStack },
    { enabled: !!gitConfig }
  );

  const hasRepoError = repoCheckQuery.isError;

  // check ref
  const refCheckQuery = useGitRefs(
    { repository: url || '', stackId, fromEdgeStack },
    { enabled: !!url && !hasRepoError }
  );

  const foundRef =
    !!reference &&
    !!refCheckQuery.data &&
    refCheckQuery.data.includes(reference);

  const hasRefError =
    !reference || refCheckQuery.isError || !refCheckQuery.data || !foundRef;

  // check file
  const enableFileCheck = stackType !== 'helm';
  const fileCheckQuery = useSearch(
    {
      repository: url || '',
      keyword: configFilePath || '',
      stackId,
      fromEdgeStack,
      reference,
    },
    !!url && !!configFilePath && !hasRefError
  );

  const foundFile =
    !!configFilePath &&
    !!fileCheckQuery.data &&
    fileCheckQuery.data.includes(configFilePath);

  const hasFileError =
    enableFileCheck &&
    (!configFilePath ||
      fileCheckQuery.isError ||
      !fileCheckQuery.data ||
      !foundFile);

  const { Interval: autoUpdateInterval, Webhook: webhook } = autoUpdate || {};
  const webhookUrl = webhook ? `${baseStackWebhookUrl()}/${webhook}` : '';

  const hasError = hasRepoError || hasRefError || hasFileError;
  const explainedError = getExplainedError(
    repoCheckQuery.error,
    refCheckQuery.error,
    fileCheckQuery.error
  );

  return (
    <Card>
      <div className="form-section-title flex gap-2 mt-0!">
        <Icon icon={GitMerge} /> Managed by Git
      </div>
      {hasError && (
        <>
          <Alert color="error" className="mb-5">
            <div className="flex flex-col">
              {hasRepoError && (
                <div>
                  The git repository <span>{url || ''}</span> could not be
                  reached.
                </div>
              )}
              {hasRefError && (
                <div>
                  The git reference <span>{reference || ''}</span> could not be
                  found on the remote repository.
                </div>
              )}
              {hasFileError && (
                <div>
                  The referenced file{' '}
                  <span className="muted">{configFilePath || ''}</span> could
                  not be found on the remote repository.
                </div>
              )}
            </div>
          </Alert>
          {!!explainedError && (
            <Alert color="info" className="mb-5">
              {explainedError}
            </Alert>
          )}
        </>
      )}

      <div
        className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-x-6 gap-y-1 text-sm"
        data-cy="helm-git-info"
      >
        {!!url && (
          <LineItem
            label="Repo:"
            value={url}
            title={url}
            isValid={!!repoCheckQuery.data && !repoCheckQuery.error}
            isError={hasRepoError}
            data-cy="git-url"
          />
        )}
        {!!reference && (
          <LineItem
            label="Ref:"
            value={reference}
            title={reference}
            isValid={foundRef}
            isError={hasRefError}
            data-cy="git-ref"
          />
        )}
        {!!configFilePath && enableFileCheck && (
          <LineItem
            label="File:"
            value={configFilePath}
            title={configFilePath}
            isValid={foundFile}
            isError={hasFileError}
            data-cy="git-file-path"
          />
        )}
        {!!commitID && (
          <LineItem
            label="Commit"
            value={<GitCommitLink baseURL={url || ''} commitHash={commitID} />}
            title={commitID}
            data-cy="git-commit"
          />
        )}
        <LineItem
          label="Auto-update:"
          value={autoUpdate ? 'On' : 'Off'}
          title="auto-update"
          data-cy="git-auto-update"
        />
        {!!autoUpdateInterval && (
          <LineItem
            label="Interval:"
            value={autoUpdateInterval}
            title="auto-update-interval"
            data-cy="git-interval"
          />
        )}
        {!!webhook && (
          <LineItem
            label="Webhook:"
            value={
              <>
                <span data-cy="git-webhook-url">
                  {truncateLeftRight(webhookUrl, 0, 10, 25)}
                </span>
                <CopyButton
                  copyText={webhookUrl}
                  color="light"
                  data-cy="git-webhook-copy-button"
                >
                  Copy link
                </CopyButton>
              </>
            }
            title="webhook"
            data-cy="git-webhook"
          />
        )}
      </div>
    </Card>
  );

  function getExplainedError(
    repoCheckErr: PortainerError | null,
    refCheckError: PortainerError | null,
    fileCheckErr: PortainerError | null
  ) {
    const errors = [repoCheckErr, refCheckError, fileCheckErr];
    if (
      errors.some(
        (s) =>
          s?.message.startsWith(
            'Authentication required: Invalid username or token'
          )
      )
    ) {
      return 'The configured Git Credentials are invalid or expired. Update the credentials to restore access.';
    }
    return '';
  }
}

function LineItem({
  label,
  isValid,
  isError,
  value,
  title,
  'data-cy': dataCy,
}: {
  label: string;
  value: ReactNode;
  title: string;
  isValid?: boolean;
  isError?: boolean;
} & AutomationTestingProps) {
  return (
    <div className="min-w-0 flex items-center gap-1">
      {isValid && <Icon icon={CheckCircle} mode="success" size="md" />}
      {isError && <Icon icon={XCircle} mode="danger" size="md" />}
      <span className="text-muted">{label}</span>
      <span
        className="truncate inline-flex align-middle items-center gap-1 flex-wrap max-w-[40rem]"
        title={title}
        data-cy={dataCy}
      >
        {value}
      </span>
    </div>
  );
}
